"use client"

import { useUserRole } from "@/providers/user-role-provider"
import { PublicPageNav } from "@/components/public-page-nav"
import { PageFrame } from "@/components/page-frame"
import { HiringBrowseShell, type JobRow } from "@/components/hiring/hiring-browse-shell"
import { OwnerHiringView, type OwnerListing } from "@/components/hiring/owner-hiring-view"

interface OwnerData {
  shops: Array<{ id: string; slug: string | null; name: string; logo_url: string | null }>
  listings: OwnerListing[]
}

interface HiringRouterProps {
  isAuthed: boolean
  publicListings: JobRow[]
  ownerData: OwnerData | null
  previewing: boolean
  /** Threaded to the browse shell for the inline role panel's own-post state. */
  currentUserId?: string | null
}

// Server fetches owner data (shops + listings) for any logged-in user.
// HiringRouter picks the surface based on the live role switcher:
//   - role="owner" + logged in + has at least one shop → OwnerHiringView.
//   - role="owner" + no shops yet → the public Job Board, with a prompt to
//     create a shop. This used to be OwnerHiringView's empty state, which
//     replaced the entire page with a full-viewport "Create a shop first"
//     wall: a barber who flipped into owner mode to look around — which is
//     exactly what the Switch Mode copy invites them to do — found nothing
//     to look at. Owning a shop is what POSTING requires (a listing carries
//     a shop_id); browsing never did.
//   - otherwise → public Job Board.
export function HiringRouter({ isAuthed, publicListings, ownerData, previewing, currentUserId = null }: HiringRouterProps) {
  const { role } = useUserRole()

  const shoplessOwner = isAuthed && ownerData && role === "owner" && ownerData.shops.length === 0

  if (isAuthed && ownerData && role === "owner" && !previewing && !shoplessOwner) {
    return <OwnerHiringView listings={ownerData.listings} shops={ownerData.shops} />
  }

  return (
    <>
      <PublicPageNav audience="barber" />
      <PageFrame chrome={isAuthed ? "app" : "public"}>
        <HiringBrowseShell
          listings={publicListings}
          previewMode={previewing}
          createShopPrompt={!!shoplessOwner && !previewing}
          isAuthed={isAuthed}
          currentUserId={currentUserId}
        />
      </PageFrame>
    </>
  )
}
